import React, { useState } from 'react';
import GetRequest from '../Services/APIRequest';
import Loading from '../Components/Loading';
import Error from '../Components/Error';

const buildTree = (list, parentId = null) =>
    list
        .filter(item => item.parentId === parentId)
        .map(item => ({ ...item, children: buildTree(list, item.id) }));

const AccordionRow = ({ item, level }) => {
    const [isOpen, setIsOpen] = useState(false);
    const hasChildren = item.children?.length > 0;

    return (
        <>
            <tr>
                <td className="text-center align-middle">
                    {hasChildren && (
                        <button
                            onClick={() => setIsOpen(!isOpen)}
                            className="btn btn-sm text-primary"
                            style={{ minWidth: 35, padding: '0.2rem 0.4rem' }}
                            aria-label={isOpen ? "Collapse" : "Expand"}
                        >
                            <i className={`fa fa-chevron-${isOpen ? 'down' : 'right'}`} />
                        </button>
                    )}
                </td>
                <td style={{ paddingLeft: `${level * 20}px`, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {item.costCentreName}
                </td>
                {/* Replace these with 'text-center' */}
                <td className="text-center">{Math.round(item.opening).toLocaleString('en-IN')}</td>
                <td className="text-center">{Math.round(item.debitAmount).toLocaleString('en-IN')}</td>
                <td className="text-center">{Math.round(item.creditAmount).toLocaleString('en-IN')}</td>
                <td className="text-center fw-bold text-success">{Math.round(item.closing).toLocaleString('en-IN')}</td>
            </tr >
            {isOpen && hasChildren &&
                item.children.map(child => (
                    <AccordionRow key={child.id} item={child} level={level + 1} />
                ))
            }
        </>
    );
};

const DistributorSummaryReport = () => {
    const distID = localStorage.getItem('distributorId');
    const { data, error, isLoaded } = GetRequest(`/Account/GetDistributorSummaryReport/${distID}`);

    if (error) return <Error data="Distributor Summary" />;
    if (!isLoaded) return <Loading />;

    const { costCenterTransactionsAndBalances, costCenterWiseNetBusinessValue, productWiseSales } = data.result;
    const tree = buildTree(costCenterTransactionsAndBalances || []);
    const groupedProducts = productWiseSales.reduce((acc, curr) => {
        if (!acc[curr.productCategory]) acc[curr.productCategory] = [];
        acc[curr.productCategory].push(curr);
        return acc;
    }, {});

    return (
        <section className="content">
            <div className="container-fluid px-2 px-md-4">
                {/* Actions & Downloads */}
                <div className="mb-3 text-end">
                    {/* Your buttons here */}
                </div>

                {/* 1. Net Business Value */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-header bg-info text-white">
                                <h5 className="card-title mb-0">1. Net Business Value (Cost Centre-wise)</h5>
                            </div>
                            <div className="card-body table-responsive p-2">
                                <table className="table table-bordered table-hover table-sm mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Sales Category</th>
                                            <th className="text-center">Net Amount</th> {/* Changed text-end to text-center */}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {costCenterWiseNetBusinessValue.map((item, idx) => (
                                            <tr key={idx}>
                                                <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{item.costCentreSalesCategoryName}</td>
                                                <td className="text-center fw-medium">{Math.round(item.netAmount).toLocaleString('en-IN')}</td> {/* Changed text-end to text-center */}
                                            </tr>
                                        ))}
                                    </tbody>

                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 2. Cost Centre Transactions Accordion */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-header bg-warning text-white">
                                <h5 className="card-title mb-0">2. Cost Centre Transactions & Balances</h5>
                            </div>
                            <div className="card-body table-responsive p-2">
                                <table className="table table-bordered table-hover table-sm mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="text-center" style={{ width: '40px' }}></th>
                                            <th>Cost Centre</th>
                                            <th className="text-center">Op</th> {/* Changed text-end to text-center */}
                                            <th className="text-center">Dr</th>   {/* Changed text-end to text-center */}
                                            <th className="text-center">Cr</th>  {/* Changed text-end to text-center */}
                                            <th className="text-center">Closing</th> {/* Changed text-end to text-center */}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tree.map(item => (
                                            <AccordionRow key={item.id} item={item} level={0} />
                                        ))}
                                    </tbody>

                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 3. Product-wise Sales */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-header bg-danger text-white">
                                <h5 className="card-title mb-0">3. Product-wise Sales Summary</h5>
                            </div>
                            <div className="card-body p-2">
                                {Object.entries(groupedProducts).map(([category, products], index) => {
                                    const sorted = [...products].sort((a, b) => a.productName.localeCompare(b.productName));
                                    const totalQty = sorted.reduce((sum, p) => sum + p.netQuantity, 0);
                                    const totalSale = sorted.reduce((sum, p) => sum + p.netSale, 0);

                                    return (
                                        <div className="mb-4" key={index}>
                                            <h6 className="fw-bold text-primary">{category}</h6>
                                            <div className="table-responsive">
                                                <table className="table table-bordered table-hover table-sm mb-0">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th>Product</th>
                                                            <th className="text-center">Net Qty</th>
                                                            <th className="text-center">Net Sale</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {sorted.map((item, i) => (
                                                            <tr key={i}>
                                                                <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{item.productName}</td>
                                                                <td className="text-center">{Math.round(item.netQuantity).toLocaleString('en-IN')}</td>
                                                                <td className="text-center fw-bold text-success">{Math.round(item.netSale).toLocaleString('en-IN')}</td>
                                                            </tr>
                                                        ))}
                                                        <tr className="table-secondary fw-bold">
                                                            <td className="text-end">Total</td>
                                                            <td className="text-center">{Math.round(totalQty).toLocaleString('en-IN')}</td>
                                                            <td className="text-center">{Math.round(totalSale).toLocaleString('en-IN')}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </section>
    );
};

export default DistributorSummaryReport;
